import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchLeads } from "../api/api";
import { useToast } from "../contexts/ToastContext";
import KPICards from "./KPICards";
import Charts, { DistributionCharts, KeyMetricsCard } from "./Charts";
import LeadsTable from "./LeadsTable";
import LeadModal from "./LeadModal";

const DEFAULT_FILTERS = {
  startDate: "",
  endDate: "",
};

const Dashboard = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [aggregations, setAggregations] = useState(null);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const { addToast } = useToast();

  const query = useMemo(() => ({
    limit,
    offset,
    startDate: filters.startDate,
    endDate: filters.endDate,
    from: filters.startDate ? `${filters.startDate}T00:00:00` : "",
    to: filters.endDate ? `${filters.endDate}T23:59:59` : "",
  }), [filters, limit, offset]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchLeads(query);
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setTotal(Number(data.total || 0));
      setAggregations(data.aggregations || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError("Не удалось загрузить данные. Проверьте REACT_APP_API_URL и деплой Apps Script.");
      addToast("Не удалось загрузить данные дашборда.", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast, query]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
    setOffset(0);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setOffset(0);
  }, []);

  const handlePageSizeChange = useCallback((nextLimit) => {
    setLimit(nextLimit);
    setOffset(0);
  }, []);

  const hasFilters = Object.entries(filters).some(([key, value]) => {
    return value && value !== "all";
  });

  return (
    <div className="space-y-7">
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="grid gap-5 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">Дата начала</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => updateFilter("startDate", event.target.value)}
              max={filters.endDate || undefined}
                className="h-14 rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </label>

          <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">Дата окончания</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => updateFilter("endDate", event.target.value)}
              min={filters.startDate || undefined}
                className="h-14 rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
                className="h-10 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Сбросить фильтры
            </button>
            )}
            <button
              type="button"
              onClick={loadData}
              disabled={loading}
              className="h-10 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {loading ? "Обновление..." : "Обновить"}
            </button>
          </div>
        </div>
      </section>

      <KPICards kpi={aggregations?.kpi} loading={loading && !aggregations} />

      <DistributionCharts aggregations={aggregations} loading={loading && !aggregations} />

      <Charts
        leadsQualityDynamics={aggregations?.leads_quality_dynamics || aggregations?.by_date || []}
        loading={loading && !aggregations}
      />

      <KeyMetricsCard aggregations={aggregations} />

      <LeadsTable
        leads={leads}
        total={total}
        limit={limit}
        offset={offset}
        onPageChange={setOffset}
        onPageSizeChange={handlePageSizeChange}
        onViewLead={setSelectedLead}
        loading={loading}
      />

      {selectedLead && (
        <LeadModal lead={selectedLead} onClose={() => setSelectedLead(null)} />
      )}
    </div>
  );
};

export default Dashboard;
